// src/pages/admin/AdminAssignAwardsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Container, Card, CardContent, Typography,
  Button, IconButton, TextField, MenuItem,
  Snackbar, Alert, LinearProgress, Tooltip, Divider
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageIcon from "@mui/icons-material/Image";
import SearchIcon from "@mui/icons-material/Search";
import api from "../../api/api";

/* ───────────────── Brand ───────────────── */
const PRIMARY = "#01548e";
const BG = "#f5f7fb";
const BORDER = "#e7ebf0";

const styles = {
  page: { minHeight: "100vh", background: BG, paddingBottom: 32 },
  card: { border: `1px solid ${BORDER}`, borderRadius: 1, background: "#fff", boxShadow: "0 8px 24px rgba(2,18,33,.06)" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: `1px solid ${BORDER}`, background: "rgba(1,84,142,.06)" },
  title: { color: PRIMARY, fontWeight: 800, margin: 0 },
  section: { padding: "12px 16px" },
  preview: { width: 160, height: 160, borderRadius: 8, border: `1px solid ${BORDER}`, objectFit: "contain", background: "#f8fafc" },
};

function useToast() {
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const toast = (m, s = "success") => setSnack({ open: true, msg: m, severity: s });
  const node = (
    <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
      <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: "100%" }}>
        {snack.msg}
      </Alert>
    </Snackbar>
  );
  return { toast, snackNode: node };
}

export default function AdminAssignAwardsPage() {
  const me = JSON.parse(localStorage.getItem("user"));
  const isAdmin = me?.role === "admin";
  const { toast, snackNode } = useToast();

  useEffect(() => { if (!isAdmin) toast("Only admins can access this page", "error"); }, [isAdmin]);

  /* ------------------ Catalog ------------------ */
  const [catalog, setCatalog] = useState([]);
  const [busyCat, setBusyCat] = useState(false);

  const loadCatalog = async () => {
    setBusyCat(true);
    try {
      const res = await api.get("/awards/catalog"); // CRM GetAllAwards
      setCatalog(res.data?.catalog || []);
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to load awards catalog", "error");
    } finally {
      setBusyCat(false);
    }
  };
  useEffect(() => { loadCatalog(); }, []);

  // Award search + selection
  const [awardid, setAwardid] = useState("");
  const [awardSearch, setAwardSearch] = useState("");
  const catalogOptions = useMemo(() => {
    const q = awardSearch.trim().toLowerCase();
    if (!q) return catalog;
    return (catalog || []).filter(c =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.awardid || "").toLowerCase().includes(q)
    );
  }, [catalog, awardSearch]);

  /* ------------------ Images: AWARD-ONLY (global) ------------------ */
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState("");
  const [busyImg, setBusyImg] = useState(false);
  const [currentGlobal, setCurrentGlobal] = useState(null);

  const onPickImg = (e) => {
    const f = e.target.files?.[0] || null;
    setImgFile(f);
    setImgPreview(f ? URL.createObjectURL(f) : "");
  };

  const loadCurrentImage = async () => {
    if (!awardid) return;
    try {
      const res = await api.get("/admin/awards/images", { params: { awardid, list: 1 } });
      setCurrentGlobal(res.data?.global || null);
    } catch {
      setCurrentGlobal(null);
    }
  };
  useEffect(() => { loadCurrentImage(); }, [awardid]);

  const saveAwardImage = async () => {
    if (!awardid) { toast("Pick an award first", "warning"); return; }
    if (!imgFile) { toast("Choose an image file", "warning"); return; }
    setBusyImg(true);
    try {
      const fd = new FormData();
      fd.append("awardid", awardid);
      fd.append("scope", "global");            // backend-compatible
      fd.append("image", imgFile);
      await api.post("/admin/awards/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast("Saved global award image");
      setImgFile(null);
      setImgPreview("");
      loadCurrentImage();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to save award image", "error");
    } finally {
      setBusyImg(false);
    }
  };

  const removeGlobalImage = async () => {
    if (!awardid) return;
    try {
      await api.delete("/admin/awards/images", { params: { awardid, scope: "global" } });
      toast("Global image removed");
      loadCurrentImage();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to remove image", "error");
    }
  };

  /* ============================== UI ============================== */
  return (
    <Box sx={styles.page}>
      <Container maxWidth="lg" sx={{ pt: 2 }}>
        <Card sx={styles.card}>
          {/* Header */}
          <div style={styles.header}>
            <h5 style={styles.title}><ImageIcon sx={{ mr: 1 }} /> Admin · Award Image</h5>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Tooltip title="Reload award catalog">
                <IconButton onClick={loadCatalog}><RefreshIcon /></IconButton>
              </Tooltip>
            </Box>
          </div>

          {(busyCat || busyImg) ? <LinearProgress /> : null}

          <CardContent sx={{ p: 0 }}>
            {/* Select award */}
            <Box sx={styles.section}>
              <Typography sx={{ color: PRIMARY, fontWeight: 800, mb: .5 }}>Select award</Typography>
              <Box sx={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <TextField
                  placeholder="Search award name or ID…"
                  value={awardSearch}
                  onChange={(e) => setAwardSearch(e.target.value)}
                  InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
                  sx={{ minWidth: 260, flex: 1 }}
                />
                <TextField
                  select label="Award" sx={{ minWidth: 360 }}
                  value={awardid} onChange={(e) => setAwardid(e.target.value)}
                >
                  {catalogOptions.map(c => <MenuItem key={c.awardid} value={c.awardid}>{c.name || c.awardid}</MenuItem>)}
                </TextField>
                <Button variant="text" startIcon={<RefreshIcon />} onClick={loadCurrentImage} disabled={!awardid}>
                  Refresh image
                </Button>
              </Box>
              <Divider sx={{ mt: 2 }} />
            </Box>

            {/* Current image */}
            {/* <Box sx={styles.section}>
              <Typography sx={{ color: PRIMARY, fontWeight: 800, mb: 1 }}>Current global image</Typography>
              <Box sx={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                {currentGlobal?.image_url ? (
                  <img src={currentGlobal.image_url} alt="global" style={styles.preview} />
                ) : (
                  <Box sx={{ ...styles.preview, display: "grid", placeItems: "center", color: "#64748b" }}>—</Box>
                )}
                <Box sx={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Button size="small" variant="outlined" onClick={removeGlobalImage} disabled={!awardid || !currentGlobal}>
                    Remove global
                  </Button>
                </Box>
              </Box>
            </Box> */}

            {/* Upload new image */}
            <Box sx={styles.section}>
              <Typography sx={{ color: PRIMARY, fontWeight: 800, mb: 1 }}>Upload new image</Typography>
              <Box sx={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                  {imgFile ? "Change image…" : "Choose image…"}
                  <input hidden accept="image/*" type="file" onChange={onPickImg} />
                </Button>
                {imgPreview ? <img src={imgPreview} alt="preview" style={styles.preview} /> : null}
                <Box sx={{ display: "flex", gap: 8 }}>
                  <Button variant="contained" onClick={saveAwardImage} disabled={!isAdmin || !awardid || busyImg || !imgFile} startIcon={<UploadIcon />}>
                    Save Global Image
                  </Button>
                  <Button variant="outlined" onClick={() => { setImgFile(null); setImgPreview(""); }} startIcon={<DeleteOutlineIcon />}>
                    Reset
                  </Button>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">
                This updates the award’s <code>global</code> image only. Recommended square image, ≥ 256×256, up to 4MB.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {snackNode}
    </Box>
  );
}
